import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import toast from "react-hot-toast";
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Chip,
    Stack,
    Divider,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import db from "state/db";
import useInflightSession from "./hooks/useInflightSession";


const STATUS_COLORS = {
    pending:   "warning",
    synced:    "success",
    failed:    "error",
    cancelled: "default",
};

const OrderHistory = ({ session }) => {
    const user = useSelector((state) => state.auth.user);
    const { cancelLocalOrder } = useInflightSession(user?.crewCode, user?.crewName);

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadOrders = useCallback(async () => {
        setLoading(true);
        try {
            if (session?._id) {
                const sessionOrders = await db.orders
                    .where("sessionId")
                    .equals(session._id.toString())
                    .filter((o) => o.syncStatus !== "cancelled")
                    .sortBy("deviceTimestamp");
                setOrders(sessionOrders.reverse());
            } else {
                const all = await db.orders
                    .filter((o) => o.syncStatus !== "cancelled")
                    .sortBy("deviceTimestamp");
                setOrders(all.reverse());
            }
        } finally {
            setLoading(false);
        }
    }, [session?._id]);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    const handleCancel = async (clientId) => {
        await cancelLocalOrder(clientId);
        loadOrders();
        toast.success("Order cancelled.");
    };

    if (loading) {
        return <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress /></Box>;
    }

    if (orders.length === 0) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert
                    severity="info"
                    sx={{ alignItems: "center"}}
                >
                    No orders recorded yet for this session.
                </Alert>
            </Box>
        );
    }

    const total = orders.reduce((s, o) => s + o.totalAmount, 0);


    return (
        <Box>
            <Box sx={{ py: 2, borderBottom: 1, borderColor: "divider" }}>
                <Stack direction="row" justifyContent="space-between">
                    <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                    >
                        {orders.length} order{orders.length !== 1 ? "s" : ""}
                    </Typography>
                    <Typography 
                        variant="subtitle2" 
                        color="primary.main"
                        fontWeight={600} 
                    >
                        Total: {total.toLocaleString()} VND
                    </Typography>
                </Stack>
            </Box>

            <List dense>
                {orders.map((order, idx) => (
                    <Box key={order.clientId}>
                        <ListItem
                            secondaryAction={
                                order.syncStatus === "pending" && (
                                    <Tooltip title="Cancel">
                                        <IconButton 
                                            size="small" 
                                            color="error" 
                                            onClick={() => handleCancel(order.clientId)}
                                        >
                                            <CancelIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )
                            }
                        >
                            <ListItemText
                                primary={
                                    <Stack 
                                        direction="row" 
                                        alignItems="center" 
                                        flexWrap="wrap"
                                        gap={1} 
                                    >
                                        <Typography variant="body2" fontWeight={600}>
                                            #{idx + 1} · {order.totalAmount.toLocaleString()} VND
                                        </Typography>
                                        <Chip
                                            label={order.syncStatus}
                                            size="small"
                                            color={STATUS_COLORS[order.syncStatus] || "default"}
                                        />
                                        <Chip 
                                            variant="outlined" 
                                            size="small" 
                                            label={order.paymentMethod} 
                                        />
                                    </Stack>
                                }
                                secondary={
                                    <Box>
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            display="block"
                                        >
                                            {order.deviceTimestamp ? format(new Date(order.deviceTimestamp), "HH:mm:ss dd/MM/yy") : ""}
                                        </Typography>
                                        {order.items.map((item, i) => (
                                            <Typography 
                                                key={i} 
                                                variant="caption" 
                                                color="text.secondary" 
                                                display="block"
                                            >
                                                · {item.productName} × {item.quantity} = {item.totalAmount.toLocaleString()} VND
                                            </Typography>
                                        ))}
                                    </Box>
                                }
                            />
                        </ListItem>
                        {idx < orders.length - 1 && <Divider />}
                    </Box>
                ))}
            </List>
        </Box>
    );
};

export default OrderHistory;
